import { Controller } from "@hotwired/stimulus"
import { baseKit } from "../shared/browserKit"
import { doit, tapChain, recall } from "../shared/functions"
// import { asArray, generateUUID } from "../shared/functions"


// 마커 상태에 따른 색상 및 이모지 반환
function getMarkerStyle(businessStatus) {
  // 재직중
  if (businessStatus === "business_status_active") {
    return {
      color: "#34A853",
      borderColor: "#2d8e47",
      emoji: "🟢"
    }
  }
  // 예비/채용예정
  if (businessStatus === "business_status_pending_hire" ||
    businessStatus === "business_status_planned_hire") {
    return {
      color: "#4285f4",
      borderColor: "#1a73e8",
      emoji: "🔵"
    }
  }
  // 심사중/심사전
  if (businessStatus === "business_status_initial_pending" ||
    businessStatus === "business_status_initial_screening" ||
    businessStatus === "business_status_review" ||
    businessStatus === "business_status_request") {
    return {
      color: "#FBBC04",
      borderColor: "#F9AB00",
      emoji: "🟡"
    }
  }
  // 기본
  return {
    color: "#34A853",
    borderColor: "#2d8e47",
    emoji: "⚫"
  }
}

// 근접한 마커들을 찾는 함수
function findNearbyMarkers(clickedMarker, allMarkers, zoom) {
  const clickedPos = clickedMarker.position

  // 줌 레벨에 따른 근접 거리 조정 (픽셀 기준)
  const pixelThreshold = zoom > 15 ? 30 : zoom > 12 ? 40 : 50
  const degreeThreshold = pixelThreshold * (360 / (256 * Math.pow(2, zoom)))

  return allMarkers.filter(marker => {
    if (marker === clickedMarker) return true

    const markerPos = marker.position
    const latDiff = Math.abs(clickedPos.lat - markerPos.lat)
    const lngDiff = Math.abs(clickedPos.lng - markerPos.lng)

    return latDiff < degreeThreshold && lngDiff < degreeThreshold
  })
}

// 거리 계산 함수 (Haversine formula)
function calculateDistance(lat1, lng1, lat2, lng2) {
  const R = 6371; // 지구 반지름 (km)
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c; // km
  return distance * 1000; // 미터로 변환
}

// 근처 locations 찾아서 DOM 이벤트 dispatch
function findNearLocationsAndDispatch(marker, allLocations, rootElement) {
  const markerLat = marker.position.lat;
  const markerLng = marker.position.lng;

  // 모든 locations에 거리 정보 추가하고 정렬
  const locationsWithDistance = allLocations.map(location => {
    const distance = calculateDistance(
      markerLat,
      markerLng,
      parseFloat(location.lat),
      parseFloat(location.lng)
    );

    return {
      ...location,
      distance: Math.round(distance), // 미터 단위, 반올림
      distanceKm: (distance / 1000).toFixed(2) // km 단위, 소수점 2자리
    };
  });

  // 거리순으로 정렬하고 상위 10개만 선택
  const nearestLocations = locationsWithDistance
    .sort((a, b) => a.distance - b.distance)
    .slice(0, 12);

  // DOM 이벤트 발생
  const event = new CustomEvent('map:cluster:marker:nearlocations', {
    detail: {
      marker: {
        lat: markerLat,
        lng: markerLng,
        title: marker.title,
        address: marker.address || "주소 정보 없음",
        position: marker.position
      },
      locations: nearestLocations
    }
  });

  rootElement.dispatchEvent(event);
}

// 마커 close 이벤트 발생 함수
function dispatchMarkerCloseEvent(marker, rootElement) {
  const event = new CustomEvent('map:cluster:marker:close', {
    detail: {
      marker: {
        lat: marker.position.lat,
        lng: marker.position.lng,
        title: marker.title,
        address: marker.address || "주소 정보 없음",
        position: marker.position
      }
    }
  });

  rootElement.dispatchEvent(event);
}

// 마커 목록 표시 함수
function showMarkerList(markers, infoWindow, map, position, rootElement) {
  const listItems = markers.map((marker, index) => {
    const locationData = marker.locationData
    const name = `#${locationData?.id || index + 1}`
    const content = locationData?.content || locationData?.title || locationData?.name || ""
    const { emoji } = getMarkerStyle(locationData?.business_status)

    return `
      <div class="marker-list-item" style="padding: 8px; border-bottom: 1px solid #eee; cursor: pointer;" data-marker-index="${index}">
        <div style="font-size:1rem;font-weight: bold; color: #333;">${emoji} ${name}</div>
        ${content ? `<div style="font-size:1rem; color: #666; margin-top: 2px;">${content}</div>` : ''}
      </div>
    `
  }).join('')

  const listContent = `
    <div style="min-width:90px; max-width: 300px; max-height: 200px; overflow-y: auto;">
      <div style="font-weight: bold; padding: 8px; border-bottom: 2px solid #ddd; background: #f5f5f5;">
        ${markers.length}개 위치
      </div>
      ${listItems}
    </div>
  `

  infoWindow.setContent(listContent)
  infoWindow.setPosition(position)
  infoWindow.open(map)

  // 클러스터 클릭 시 근처 locations 이벤트 발생
  const markerLocations = markers.map(marker => ({
    ...marker.locationData,
    distance: 0,
    distanceKm: "0.00"
  }))

  const event = new CustomEvent('map:cluster:marker:nearlocations', {
    detail: {
      marker: {
        lat: position.lat,
        lng: position.lng,
        title: `${markers.length}개 위치`,
        address: "클러스터 위치",
        position: position
      },
      locations: markerLocations
    }
  })

  rootElement.dispatchEvent(event)

  // 목록 아이템 클릭 이벤트 (인포윈도우가 열린 후 추가)
  setTimeout(() => {
    const listItemElements = document.querySelectorAll('.marker-list-item')
    listItemElements.forEach((item, index) => {
      item.addEventListener('click', () => {
        const marker = markers[index]
        const locationData = marker.locationData
        const content = `
          <div>
            <h3>#${locationData.id || '?'}</h3>
            <p>${locationData.name || '이름 없음'}</p>
            <p>${locationData?.phone || '전화번호 없음'}</p>
          </div>
        `

        infoWindow.setContent(content)
        infoWindow.setPosition(marker.position)
        infoWindow.open(map)

        // 해당 마커 위치로 지도 중심 이동
        map.panTo(marker.position)
      })
    })
  }, 100)
}

// Connects to :: <section x-ignore v-pre data-controller="petite-vue">
// Script :: <script data-petite-vue-target="setup" >
export default baseKit(({ dispatchElement }) => {
  const loadOptions = {
    apiKey: "AIzaSyCRLqccYVyDvpElEqaJNHgg4bsOzwBRUIM",
    version: "quarterly",
    libraries: ["maps", "geocoding", "marker", "places", "elevation", "geometry"]
  }

  return class extends Controller {

    // static targets = [
    //   "view",
    // ]

    static values = {
      // data-map-cluster-locations-value="[]" => this.locationsValue
      locations: { type: Array, default: [] },
    }

    connect() {
      const controller = this
      const rootElement = this.element

      const koreaBounds = {
        north: 39.15157738781489,
        south: 32.753690214482596,
        east: 132.0,
        west: 124.5
      }

      const mapOptions = {
        // mapId: Platform > 지도관리 > 생성
        mapId: 'bc0eaf137b35c23e67e4d5f4',
        restriction: {
          latLngBounds: koreaBounds,
          strictBounds: false // true로 바꾸면 더 엄격하게 제한
        },
        center: { lat: 37.51591975564616, lng: 127.05801628482645 },
        zoom: 9,
        streetViewControl: false,
        mapTypeControl: false,
      }

      const mapContainer = document.createElement("div")
      mapContainer.id = "map2"
      mapContainer.style.width = "100%"
      mapContainer.style.height = "400px"
      rootElement.appendChild(mapContainer)

      import("@googlemaps/js-api-loader").then((jsApi) => {
        doit(new jsApi.Loader(loadOptions)).load()
          .then((google) => {
            const map = new google.maps.Map(mapContainer, mapOptions)
            const elevation = new google.maps.ElevationService()
            const geocoder = new google.maps.Geocoder()
            return tapChain({ google, map, elevation, geocoder })
          })
          .then(
            // 마커와 마커 윈도우
            recall(async (props) => {
              const { google } = props
              const searchedMarker = new google.maps.marker.AdvancedMarkerElement({
                position: mapOptions.center,
                map: null,
                title: "시작위치",
              })

              const searchedMarkerWindow = new google.maps.InfoWindow({});

              // InfoWindow close 이벤트 리스너 추가
              searchedMarkerWindow.addListener('closeclick', () => {
                // 마커 제거
                searchedMarker.map = null;

                // close 이벤트 발생
                dispatchMarkerCloseEvent(searchedMarker, rootElement);
              });

              props.searchedMarker = searchedMarker
              props.searchedMarkerWindow = searchedMarkerWindow
            }
            ))
          .then(
            // 마커 클러스터 생성  
            recall(async (props) => {
              const { google, map } = props

              // marker 라이브러리에서 AdvancedMarkerElement와 PinElement 가져오기
              const { AdvancedMarkerElement, PinElement } = await google.maps.importLibrary("marker")

              // CDN에서 MarkerClusterer 라이브러리 import
              const { MarkerClusterer } = await import("https://cdn.jsdelivr.net/npm/@googlemaps/markerclusterer@2.5.3/+esm")

              // InfoWindow 생성
              const infoWindow = new google.maps.InfoWindow({
                content: "",
                disableAutoPan: true,
              })

              // InfoWindow가 열릴 때 searchedMarker 제거
              google.maps.event.addListener(infoWindow, 'domready', () => {
                if (props.searchedMarker && props.searchedMarker.map) {
                  props.searchedMarker.map = null
                }
              })

              // 위치 데이터에서 마커 생성
              const markers = controller.locationsValue.map((location, i) => {
                const { color: markerColor, borderColor } = getMarkerStyle(location.business_status)

                // 커스텀 HTML 마커 생성 (글씨 크기 조절 가능)
                const markerDiv = document.createElement('div')
                markerDiv.style.cssText = `
                  position: relative;
                  width: 32px;
                  height: 40px;
                  cursor: pointer;
                `

                // SVG 핀 모양
                markerDiv.innerHTML = `
                  <svg width="32" height="40" viewBox="0 0 32 40" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 0C9.4 0 4 5.4 4 12c0 8 12 28 12 28s12-20 12-28c0-6.6-5.4-12-12-12z" 
                          fill="${markerColor}" 
                          stroke="${borderColor}" 
                          stroke-width="2"/>
                  </svg>
                  <div style="
                    position: absolute;
                    top: 6px;
                    left: 0;
                    width: 32px;
                    text-align: center;
                    color: white;
                    font-size: 14px;
                    font-weight: 600;
                    pointer-events: none;
                  ">#${location.id}</div>
                `

                const marker = new AdvancedMarkerElement({
                  position: { lat: parseFloat(location.lat), lng: parseFloat(location.lng) },
                  content: markerDiv,
                  title: location.title || location.name || "위치"
                })

                // 마커에 원본 위치 데이터 저장
                marker.locationData = location

                // 마커 클릭 이벤트 - 근접한 마커들 확인
                marker.addListener("click", () => {
                  const nearbyMarkers = findNearbyMarkers(marker, markers, map.getZoom())

                  if (nearbyMarkers.length > 1) {
                    // 근접한 마커가 여러 개면 목록 표시
                    showMarkerList(nearbyMarkers, infoWindow, map, marker.position, rootElement)
                  } else {
                    // 단일 마커면 일반 정보 표시
                    const content = `
                    <div>
                      <h3>#${location.id || '?'}</h3>
                      <p>${location.name || '이름 없음'}</p>
                      <p>${location.phone || '전화번호 없음'}</p>
                    </div>
                  `
                    infoWindow.setContent(content)
                    infoWindow.open(map, marker)
                  }
                })

                return marker
              })

              // 커스텀 클러스터 렌더러 (동적 색상)
              const renderer = {
                render: ({ count, position, markers }, stats, map) => {
                  // 클러스터 내 마커들의 business_status 분석
                  const statusCounts = { active: 0, pending: 0, initial: 0 }

                  markers.forEach(marker => {
                    const status = marker.locationData?.business_status
                    if (status === "business_status_active") {
                      statusCounts.active++
                    } else if (status === "business_status_pending_hire" ||
                      status === "business_status_planned_hire") {
                      statusCounts.pending++
                    } else if (status === "business_status_initial_pending" ||
                      status === "business_status_initial_screening" ||
                      status === "business_status_review" ||
                      status === "business_status_request") {
                      statusCounts.initial++
                    }
                  })

                  // 비율 계산
                  const total = statusCounts.active + statusCounts.pending + statusCounts.initial
                  const ratios = {
                    active: total > 0 ? statusCounts.active / total : 0,
                    pending: total > 0 ? statusCounts.pending / total : 0,
                    initial: total > 0 ? statusCounts.initial / total : 0
                  }

                  let svg

                  // 순수 상태 체크 (하나의 상태만 존재)
                  if (ratios.active === 1) {
                    svg = createSolidCircleSVG("#34A853")
                  } else if (ratios.pending === 1) {
                    svg = createSolidCircleSVG("#4285f4")
                  } else if (ratios.initial === 1) {
                    svg = createSolidCircleSVG("#FBBC04")
                  } else {
                    // 혼합 상태 - 파이 차트
                    svg = createPieChartSVG(ratios)
                  }

                  return new google.maps.Marker({
                    position,
                    icon: {
                      url: `data:image/svg+xml;base64,${svg}`,
                      scaledSize: new google.maps.Size(46, 46),
                    },
                    label: {
                      text: String(count),
                      color: "rgba(255,255,255,1)",
                      fontSize: "15px",
                      fontWeight: "bold",
                    },
                    zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
                  })
                },
              }

              // 단색 원형 SVG 생성
              function createSolidCircleSVG(color) {
                return window.btoa(`
                  <svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
                    <circle cx="120" cy="120" opacity="0.95" r="70" />
                    <circle cx="120" cy="120" opacity="0.7" r="90" />
                    <circle cx="120" cy="120" opacity="0.5" r="110" />
                    <circle cx="120" cy="120" opacity="0.3" r="130" />
                  </svg>`)
              }

              // 파이 차트 SVG 생성
              function createPieChartSVG(ratios) {
                const colors = {
                  active: "#34A853",
                  pending: "#4285f4",
                  initial: "#FBBC04"
                }

                // 파이 차트 경로 생성 함수 (각도 기반)
                function createPieSlice(startAngle, endAngle, radius, cx, cy) {
                  const start = polarToCartesian(cx, cy, radius, endAngle)
                  const end = polarToCartesian(cx, cy, radius, startAngle)
                  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1"
                  return [
                    "M", cx, cy,
                    "L", start.x, start.y,
                    "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y,
                    "Z"
                  ].join(" ")
                }

                function polarToCartesian(cx, cy, radius, angleInDegrees) {
                  const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0
                  return {
                    x: cx + (radius * Math.cos(angleInRadians)),
                    y: cy + (radius * Math.sin(angleInRadians))
                  }
                }

                let currentAngle = 0
                let pieSlices = ''
                const radii = [130, 110, 90, 70]
                const opacities = [0.3, 0.5, 0.7, 0.95]

                // 각 상태별로 슬라이스 생성
                Object.entries(ratios).forEach(([type, ratio]) => {
                  if (ratio > 0) {
                    const angle = ratio * 360
                    radii.forEach((radius, idx) => {
                      pieSlices += `<path d="${createPieSlice(currentAngle, currentAngle + angle, radius, 120, 120)}" fill="${colors[type]}" opacity="${opacities[idx]}"/>`
                    })
                    currentAngle += angle
                  }
                })

                return window.btoa(`
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
                    ${pieSlices}
                  </svg>`)
              }

              // 마커 클러스터러 생성 (클릭 시 줌 비활성화)
              const clusterer = new MarkerClusterer({
                markers,
                map,
                renderer: renderer, // 커스텀 렌더러 추가
                onClusterClick: (event, cluster, map) => {
                  // 클러스터 클릭 시 목록 표시 (줌 대신)
                  const clusterMarkers = cluster.markers
                  showMarkerList(clusterMarkers, infoWindow, map, cluster.position, rootElement)
                  return false // 기본 줌 동작 방지
                }
              })

              props.clusterer = clusterer
              props.infoWindow = infoWindow
            })
          )
          .then(
            // 검색
            recall(async ({ google, map, searchedMarker, searchedMarkerWindow }) => {
              await google.maps.importLibrary("places");
              const gmpPlaceSearchElement = new google.maps.places.PlaceAutocompleteElement({
                includedRegionCodes: ['kr'] // 대한민국만 검색
              });
              rootElement.appendChild(gmpPlaceSearchElement);

              // 검색 이벤트
              gmpPlaceSearchElement.addEventListener('gmp-select', async (event) => {
                const { placePrediction } = event
                const place = placePrediction.toPlace();
                await place.fetchFields({ fields: ['displayName', 'formattedAddress', 'location', 'types'] });

                // 마커 위치 및 표시 업데이트
                searchedMarker.position = place.location;
                searchedMarker.title = place.displayName || place.formattedAddress || "";
                searchedMarker.address = place.formattedAddress || "주소 정보 없음";
                searchedMarker.map = map; // 지도에 표시

                // map_search_controller.js와 동일한 정보창 내용
                searchedMarkerWindow.setContent(`
                <div style="background-color: rgba(255, 255, 255, 0.6); border-radius: 5px;">
                  <h3>${place.displayName || place.formattedAddress || ""}</h3>
                  <p>${place.formattedAddress || ''}</p>
                </div>
              `);

                // 마커 클릭 이벤트 - 근처 locations 찾기
                google.maps.event.clearListeners(searchedMarker, 'click');
                searchedMarker.addListener("click", () => {
                  searchedMarkerWindow.open({
                    map,
                    anchor: searchedMarker,
                    shouldFocus: false,
                  })

                  // 근처 locations 찾아서 이벤트 발생
                  findNearLocationsAndDispatch(searchedMarker, controller.locationsValue, rootElement)
                })

                searchedMarkerWindow.open({
                  map,
                  anchor: searchedMarker,
                  shouldFocus: false,
                })

                map.setCenter(place.location);
                map.setZoom(14);

                const marker_location = {
                  display_name: place.displayName,
                  formatted_address: place.formattedAddress,
                  place_types: place.types,
                  lat: place.location.lat(),
                  lng: place.location.lng(),
                  zoom: map.getZoom(),
                }

                // 근처 locations 찾아서 이벤트 발생
                findNearLocationsAndDispatch(searchedMarker, controller.locationsValue, rootElement)
              });
            }
            ))
          .then(
            // 지형 클릭시 마커 생성 (map_search_controller.js와 동일)
            recall(async ({ google, map, searchedMarker, searchedMarkerWindow, geocoder, infoWindow }) => {
              map.addListener("click", async (event) => {
                // 기존 InfoWindow 닫기 (클러스터/마커 목록 제거)
                infoWindow.close()

                // 기존 마커가 있으면 지형 클릭 무시
                // if (searchedMarker.map) {
                //   console.log("기존 마커가 있어서 지형 클릭을 무시합니다.")
                //   return;
                // }

                const zoom = map.getZoom()
                const lat = event.latLng.lat()
                const lng = event.latLng.lng()

                // 기본 정보창 방지
                if (event.placeId) {
                  event.stop();
                }

                console.log("Zoom:", zoom, "Lat:", lat, "Lng:", lng)

                geocoder.geocode({ location: event.latLng }, (results, status) => {
                  if (status === 'OK' && results[0]) {
                    const place = results[0]

                    // 행정구역 displayName 추출 함수
                    function extractAdministrativeName(addressComponents) {
                      let province = ''
                      let city = ''
                      let district = ''
                      let sublocality = ''
                      let village = ''
                      addressComponents.forEach(component => {
                        const types = component.types
                        if (types.includes('administrative_area_level_1')) {
                          province = component.long_name
                        } else if (types.includes('administrative_area_level_2')) {
                          city = component.long_name
                        } else if (types.includes('administrative_area_level_3')) {
                          district = component.long_name
                        } else if (types.includes('sublocality_level_1') || types.includes('administrative_area_level_4')) {
                          sublocality = component.long_name
                        } else if (types.includes('sublocality_level_2') || types.includes('administrative_area_level_5')) {
                          village = component.long_name
                        }
                      })
                      // display_name: "xx구 xx동" 또는 "xx시 xx동" 등
                      if (district && sublocality) return `${district} ${sublocality}`
                      if (city && sublocality) return `${city} ${sublocality}`
                      if (sublocality) return sublocality
                      if (district) return district
                      if (city) return city
                      if (village) return village
                      return ''
                    }

                    // 행정구역 전체 주소 생성 함수
                    function administrativeAddress(addressComponents) {
                      let province = ''
                      let city = ''
                      let district = ''
                      let sublocality = ''
                      let village = ''
                      addressComponents.forEach(component => {
                        const types = component.types
                        if (types.includes('administrative_area_level_1')) {
                          province = component.long_name
                        } else if (types.includes('administrative_area_level_2')) {
                          city = component.long_name
                        } else if (types.includes('administrative_area_level_3')) {
                          district = component.long_name
                        } else if (types.includes('sublocality_level_1') || types.includes('administrative_area_level_4')) {
                          sublocality = component.long_name
                        } else if (types.includes('sublocality_level_2') || types.includes('administrative_area_level_5')) {
                          village = component.long_name
                        }
                      })
                      const parts = []
                      if (province) parts.push(province)
                      if (city) parts.push(city)
                      if (district) parts.push(district)
                      if (sublocality) parts.push(sublocality)
                      if (village) parts.push(village)
                      return parts.join(' ')
                    }

                    let displayName, formattedAddress

                    if (zoom < 14) {
                      // 14 미만: 행정구역까지만
                      formattedAddress = administrativeAddress(place.address_components)
                      displayName = extractAdministrativeName(place.address_components) || formattedAddress
                    } else {
                      // 14 이상: displayName은 동/리까지만, formattedAddress는 번지수까지 전체 주소
                      displayName = extractAdministrativeName(place.address_components) || place.formatted_address
                      formattedAddress = place.formatted_address
                    }

                    searchedMarker.position = event.latLng;
                    searchedMarker.title = displayName;
                    searchedMarker.address = formattedAddress;
                    searchedMarker.map = map;

                    searchedMarkerWindow.setContent(`
                    <div style="background-color: rgba(255, 255, 255, 0.6); border-radius: 5px;">
                      <h3>${displayName}</h3>
                      <p>${formattedAddress}</p>
                    </div>
                  `);

                    google.maps.event.clearListeners(searchedMarker, 'click');
                    searchedMarker.addListener("click", () => {
                      searchedMarkerWindow.open({
                        map,
                        anchor: searchedMarker,
                        shouldFocus: false,
                      })

                      // 근처 locations 찾아서 이벤트 발생
                      findNearLocationsAndDispatch(searchedMarker, controller.locationsValue, rootElement)
                    })

                    searchedMarkerWindow.open({
                      map,
                      anchor: searchedMarker,
                      shouldFocus: false,
                    })

                    // 근처 locations 찾아서 이벤트 발생
                    findNearLocationsAndDispatch(searchedMarker, controller.locationsValue, rootElement)
                  } else {
                    // 주소를 찾을 수 없는 경우 기존 함수 사용
                    handleEmptyLocation(event.latLng, lat, lng, zoom)
                  }
                })

                // 공통 함수: 빈 공간 처리
                function handleEmptyLocation(latLng, lat, lng, zoom) {
                  searchedMarker.position = latLng;
                  searchedMarker.title = "선택된 위치";
                  searchedMarker.address = `위도: ${lat.toFixed(6)}, 경도: ${lng.toFixed(6)}`;
                  searchedMarker.map = map;

                  searchedMarkerWindow.setContent(`
                  <div style="background-color: rgba(255, 255, 255, 0.6); border-radius: 5px;">
                    <h3>선택된 위치</h3>
                    <p>주소가 없는 위치</p>
                    <p>위도: ${lat.toFixed(6)}</p>
                    <p>경도: ${lng.toFixed(6)}</p>
                  </div>
                `);

                  google.maps.event.clearListeners(searchedMarker, 'click');
                  searchedMarker.addListener("click", () => {
                    searchedMarkerWindow.open({
                      map,
                      anchor: searchedMarker,
                      shouldFocus: false,
                    })

                    // 근처 locations 찾아서 이벤트 발생
                    findNearLocationsAndDispatch(searchedMarker, controller.locationsValue, rootElement)
                  })

                  // 정보창 자동으로 열기
                  searchedMarkerWindow.open({
                    map,
                    anchor: searchedMarker,
                    shouldFocus: false,
                  })

                  // 근처 locations 찾아서 이벤트 발생
                  findNearLocationsAndDispatch(searchedMarker, controller.locationsValue, rootElement)
                }
              })
            }
            ))
      })
    }
  }
})
